import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import AppImage from '../../../components/AppImage';

const MatchHistory = ({ matches, onNavigate }) => {
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');

  const filters = [
    { id: 'all', label: 'Todas', count: matches?.length },
    { id: 'wins', label: 'Vitórias', count: matches?.filter(m => m?.result === 'win')?.length },
    { id: 'losses', label: 'Derrotas', count: matches?.filter(m => m?.result === 'loss')?.length },
    { id: 'pending', label: 'Agendadas', count: matches?.filter(m => m?.result === 'pending')?.length }
  ];

  const sortOptions = [
    { value: 'date', label: 'Data' },
    { value: 'result', label: 'Resultado' },
    { value: 'goals', label: 'Gols' }
  ];

  const getResultIcon = (result) => {
    switch (result) {
      case 'win': return 'TrendingUp';
      case 'loss': return 'TrendingDown';
      case 'draw': return 'Minus';
      default: return 'Clock';
    }
  };

  const getResultColor = (result) => {
    switch (result) {
      case 'win': return 'text-success bg-success/10';
      case 'loss': return 'text-destructive bg-destructive/10';
      case 'draw': return 'text-warning bg-warning/10';
      default: return 'text-muted-foreground bg-muted/50';
    }
  };

  const getResultText = (result) => {
    switch (result) {
      case 'win': return 'Vitória';
      case 'loss': return 'Derrota';
      case 'draw': return 'Empate';
      default: return 'Agendada';
    }
  };

  const filteredMatches = matches?.filter(match => {
    if (filter === 'all') return true;
    if (filter === 'wins') return match?.result === 'win';
    if (filter === 'losses') return match?.result === 'loss';
    if (filter === 'pending') return match?.result === 'pending';
    return true;
  });

  const sortedMatches = filteredMatches?.sort((a, b) => {
    switch (sortBy) {
      case 'date':
        return new Date(b?.date) - new Date(a?.date);
      case 'result':
        const resultOrder = { win: 3, draw: 2, loss: 1, pending: 0 };
        return resultOrder?.[b?.result] - resultOrder?.[a?.result];
      case 'goals':
        return (b?.userGoals || 0) - (a?.userGoals || 0);
      default:
        return 0;
    }
  });

  const totalStats = {
    matches: matches?.length || 0,
    wins: matches?.filter(m => m?.result === 'win')?.length || 0,
    goals: matches?.reduce((sum, m) => sum + (m?.userGoals || 0), 0) || 0,
    assists: matches?.reduce((sum, m) => sum + (m?.userAssists || 0), 0) || 0
  };

  const winRate = totalStats?.matches > 0 ? (totalStats?.wins / totalStats?.matches * 100) : 0;

  return (
    <div className="bg-card border border-border rounded-lg shadow-elevation-2 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading font-bold text-lg text-card-foreground">
            Histórico de Partidas
          </h2>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onNavigate?.('/match-details')}
            iconName="Plus"
            iconPosition="left"
            iconSize={16}
          >
            Nova Partida
          </Button>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          <div className="text-center bg-muted/30 rounded-lg p-3">
            <div className="text-xl font-bold text-card-foreground">{totalStats?.matches}</div>
            <div className="text-xs text-muted-foreground">Partidas</div>
          </div>
          <div className="text-center bg-success/10 rounded-lg p-3">
            <div className="text-xl font-bold text-success">{totalStats?.wins}</div>
            <div className="text-xs text-muted-foreground">Vitórias</div>
          </div>
          <div className="text-center bg-primary/10 rounded-lg p-3">
            <div className="text-xl font-bold text-primary">{totalStats?.goals}</div>
            <div className="text-xs text-muted-foreground">Gols</div>
          </div>
          <div className="text-center bg-secondary/10 rounded-lg p-3">
            <div className="text-xl font-bold text-secondary">{winRate?.toFixed(0)}%</div>
            <div className="text-xs text-muted-foreground">Taxa</div>
          </div>
        </div>

        {/* Filters and Sort */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex space-x-1">
            {filters?.map((filterOption) => (
              <button
                key={filterOption?.id}
                onClick={() => setFilter(filterOption?.id)}
                className={`px-3 py-1 text-xs font-medium rounded transition-standard ${
                  filter === filterOption?.id
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:text-card-foreground'
                }`}
              >
                {filterOption?.label} ({filterOption?.count})
              </button>
            ))}
          </div>
          
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e?.target?.value)}
            className="px-3 py-1 text-xs border border-input rounded bg-background text-foreground"
          >
            {sortOptions?.map((option) => (
              <option key={option?.value} value={option?.value}>
                Ordenar por {option?.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      {/* Match List */}
      <div className="max-h-96 overflow-y-auto">
        {sortedMatches?.length > 0 ? (
          <div className="divide-y divide-border">
            {sortedMatches?.map((match) => (
              <div key={match?.id} className="p-4 hover:bg-muted/30 transition-standard">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <div className={`p-2 rounded-lg ${getResultColor(match?.result)}`}>
                        <Icon name={getResultIcon(match?.result)} size={16} />
                      </div>
                      <div>
                        <h3 className="font-medium text-card-foreground">{match?.title}</h3>
                        <p className="text-sm text-muted-foreground">{match?.venue}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                        <div className="flex items-center space-x-1">
                          <Icon name="Calendar" size={14} />
                          <span>{new Date(match?.date)?.toLocaleDateString('pt-BR')}</span>
                        </div>
                        <div className="flex items-center space-x-1">
                          <Icon name="Clock" size={14} />
                          <span>{match?.time}</span>
                        </div>
                      </div>
                      
                      <div className="text-right">
                        <div className="text-sm font-medium text-card-foreground">
                          {getResultText(match?.result)}
                        </div>
                        {match?.score && (
                          <div className="text-xs text-muted-foreground">
                            {match?.score}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Performance Stats */}
                    {(match?.userGoals > 0 || match?.userAssists > 0) && (
                      <div className="flex items-center space-x-4 mt-2 pt-2 border-t border-border/50">
                        {match?.userGoals > 0 && (
                          <div className="flex items-center space-x-1 text-xs text-primary">
                            <Icon name="Target" size={12} />
                            <span>{match?.userGoals} gols</span>
                          </div>
                        )}
                        {match?.userAssists > 0 && (
                          <div className="flex items-center space-x-1 text-xs text-secondary">
                            <Icon name="Users" size={12} />
                            <span>{match?.userAssists} assistências</span>
                          </div>
                        )}
                        <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                          <Icon 
                            name={match?.payment === 'paid' ? 'CheckCircle' : 'AlertCircle'} 
                            size={12} 
                            className={match?.payment === 'paid' ? 'text-success' : 'text-warning'} 
                          />
                          <span>{match?.payment === 'paid' ? 'Pago' : 'Pendente'}</span>
                        </div>
                      </div>
                    )}

                    {/* Match Photos */}
                    {match?.photos?.length > 0 && (
                      <div className="flex space-x-2 mt-3">
                        {match?.photos?.slice(0, 3)?.map((photo, index) => (
                          <AppImage
                            key={index}
                            src={photo?.url}
                            alt={photo?.alt}
                            className="w-12 h-12 rounded-lg object-cover"
                          />
                        ))}
                        {match?.photos?.length > 3 && (
                          <div className="w-12 h-12 rounded-lg bg-muted/50 flex items-center justify-center text-xs text-muted-foreground">
                            +{match?.photos?.length - 3}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  
                  <div className="ml-4">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onNavigate?.(`/match-details/${match?.id}`)}
                      iconName="ExternalLink"
                      iconPosition="left"
                      iconSize={14}
                    >
                      Ver
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="flex items-center justify-center w-16 h-16 bg-muted/50 rounded-lg mx-auto mb-4">
              <Icon name="Calendar" size={24} className="text-muted-foreground" />
            </div>
            <h3 className="font-heading font-semibold text-card-foreground mb-2">
              {filter === 'all' ? 'Nenhuma partida encontrada' : `Nenhuma ${filters?.find(f => f?.id === filter)?.label?.toLowerCase()} encontrada`}
            </h3>
            <p className="text-muted-foreground mb-4">
              {filter === 'all' ?'Comece participando de peladas para ver seu histórico aqui' :'Tente alterar o filtro para ver outras partidas'
              }
            </p>
            <Button
              variant="default"
              onClick={() => onNavigate?.('/venue-search')}
              iconName="Search"
              iconPosition="left"
              iconSize={16}
            >
              Buscar Peladas
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MatchHistory;